
import React from 'react';

export default function OxygenChallengeSection() {
  const challengeStats = [
    {
      icon: 'ri-heart-pulse-line',
      stat: '200+',
      label: 'Medical Conditions',
      description: 'Linked to oxygen deficiency'
    },
    {
      icon: 'ri-user-line',
      stat: 'Millions',
      label: 'People Affected',
      description: 'In India suffer from oxygen-related issues'
    },
    {
      icon: 'ri-hospital-line',
      stat: 'Preventable',
      label: 'Health Burden',
      description: 'Diseases caused by oxygen deficiency'
    }
  ];

  const symptoms = [
    'Chronic fatigue and low energy',
    'Breathlessness during daily activities',
    'Poor sleep quality and concentration',
    'Frequent respiratory infections',
    'Reduced physical performance',
    'Mental fog and cognitive decline'
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#111111] mb-6">
            The Oxygen Deficiency Challenge
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed">
            200+ conditions are linked to oxygen deficiency. Millions in India suffer from 
            fatigue, breathlessness, and preventable diseases.
          </p>
        </div>

        {/* Challenge Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {challengeStats.map((item, index) => (
            <div key={index} className="text-center bg-[#FAFAFA] rounded-3xl p-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#12B6B6] rounded-2xl flex items-center justify-center">
                <i className={`${item.icon} text-2xl text-white`}></i>
              </div>
              <div className="text-3xl md:text-4xl font-bold text-[#111111] mb-2">
                {item.stat}
              </div>
              <div className="text-lg font-semibold text-[#111111] mb-2">
                {item.label}
              </div>
              <div className="text-sm text-gray-600">
                {item.description}
              </div>
            </div>
          ))}
        </div>

        {/* Impact Flow Visualization */}
        <div className="bg-gradient-to-r from-gray-50 to-[#12B6B6]/5 rounded-3xl p-8 md:p-12">
          <h3 className="text-2xl font-bold text-[#111111] text-center mb-12">
            The Impact Chain
          </h3>
          
          <div className="flex flex-col md:flex-row items-center justify-between space-y-8 md:space-y-0 md:space-x-8">
            {/* Step 1: Oxygen Deficiency */}
            <div className="flex-1 text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-red-100 rounded-3xl flex items-center justify-center">
                <i className="ri-lungs-line text-3xl text-red-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-[#111111] mb-2">Oxygen Deficiency</h4>
              <p className="text-sm text-gray-600">Poor air quality, lifestyle factors, and genetic predisposition</p>
            </div>

            {/* Arrow */}
            <div className="hidden md:block">
              <i className="ri-arrow-right-line text-2xl text-gray-400"></i>
            </div>
            <div className="md:hidden">
              <i className="ri-arrow-down-line text-2xl text-gray-400"></i>
            </div>

            {/* Step 2: Health Impact */}
            <div className="flex-1 text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-orange-100 rounded-3xl flex items-center justify-center">
                <i className="ri-heart-line text-3xl text-orange-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-[#111111] mb-2">Health Impact</h4>
              <div className="text-left max-w-xs mx-auto">
                {symptoms.slice(0, 3).map((symptom, idx) => (
                  <div key={idx} className="text-sm text-gray-600 mb-1">• {symptom}</div>
                ))}
                <div className="text-xs text-gray-500 mt-2">+3 more symptoms</div>
              </div>
            </div>

            {/* Arrow */}
            <div className="hidden md:block">
              <i className="ri-arrow-right-line text-2xl text-gray-400"></i>
            </div>
            <div className="md:hidden">
              <i className="ri-arrow-down-line text-2xl text-gray-400"></i>
            </div>

            {/* Step 3: Community Burden */}
            <div className="flex-1 text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-purple-100 rounded-3xl flex items-center justify-center">
                <i className="ri-community-line text-3xl text-purple-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-[#111111] mb-2">Community Burden</h4>
              <p className="text-sm text-gray-600">Reduced productivity, healthcare costs, and quality of life</p>
            </div>
          </div>

          {/* Solution Arrow */}
          <div className="text-center mt-12">
            <div className="inline-flex items-center bg-[#12B6B6] text-white px-6 py-3 rounded-full">
              <i className="ri-lightbulb-line mr-2"></i>
              <span className="font-medium">Our Solution Breaks This Chain</span>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <h3 className="text-2xl font-bold text-[#111111] mb-4">
            It's Time for Change
          </h3>
          <p className="text-gray-700 max-w-2xl mx-auto mb-8">
            Through early detection, education, and targeted supplementation, we can prevent 
            oxygen deficiency from becoming a lifelong health burden.
          </p>
        </div>
      </div>
    </section>
  );
}
